import { useEffect, useRef, useState, useCallback } from 'react'
import { getWsUrl, getApiBase, lerpArray } from './utils.js'

export function useDreamSocket() {
  const [dream, setDream] = useState(null)
  const [prevDream, setPrevDream] = useState(null)
  const [status, setStatus] = useState(null)
  const [learning, setLearning] = useState(null)
  const [memories, setMemories] = useState([])
  const [connected, setConnected] = useState(false)
  const wsRef = useRef(null)

  const connect = useCallback(() => {
    const ws = new WebSocket(getWsUrl())
    wsRef.current = ws
    ws.onopen = () => setConnected(true)
    ws.onclose = () => {
      setConnected(false)
      setTimeout(connect, 3000)
    }
    ws.onerror = () => ws.close()
    ws.onmessage = (event) => {
      const msg = JSON.parse(event.data)
      if (msg.type === 'init') {
        if (msg.dream) setDream(msg.dream)
        if (msg.status) setStatus(msg.status)
        if (msg.learning) setLearning(msg.learning)
        if (msg.memories) setMemories(msg.memories)
      } else if (msg.type === 'dream') {
        setDream((current) => {
          if (current) setPrevDream(current)
          return msg.dream
        })
        if (msg.dream?.generation != null) {
          setStatus((s) =>
            s ? { ...s, generation: msg.dream.generation, status: 'DREAMING' } : s
          )
        }
      } else if (msg.type === 'memory') {
        setMemories(msg.memories)
      } else if (msg.type === 'learning') {
        setLearning(msg.learning)
      } else if (msg.type === 'status') {
        setStatus(msg.status)
      } else if (msg.type === 'evolution_update') {
        // observational — instrument poll refreshes evolution
      }
    }
  }, [])

  useEffect(() => {
    connect()
    return () => wsRef.current?.close()
  }, [connect])

  useEffect(() => {
    const poll = () => {
      fetch(`${getApiBase()}/api/status`)
        .then((r) => (r.ok ? r.json() : null))
        .then((data) => {
          if (data) setStatus(data)
        })
        .catch(() => {})
    }
    poll()
    const id = setInterval(poll, 15000)
    return () => clearInterval(id)
  }, [])

  return { dream, prevDream, status, learning, memories, connected }
}

export function useDreamMorph(dream, prevDream, duration = 800) {
  const [displaySequence, setDisplaySequence] = useState(null)
  const [displayAscii, setDisplayAscii] = useState(null)
  const rafRef = useRef(null)

  useEffect(() => {
    if (!dream) return
    const fromSeq = prevDream?.sequence || dream.sequence
    const toSeq = dream.sequence
    const fromAscii = prevDream?.ascii_pattern || dream.ascii_pattern
    const toAscii = dream.ascii_pattern
    const start = performance.now()

    function animate(now) {
      const t = Math.min((now - start) / duration, 1)
      const eased = t * t * (3 - 2 * t)
      setDisplaySequence(lerpArray(fromSeq, toSeq, eased))
      if (t < 1) {
        setDisplayAscii(eased < 0.5 ? fromAscii : toAscii)
        rafRef.current = requestAnimationFrame(animate)
      } else {
        setDisplayAscii(toAscii)
        setDisplaySequence(toSeq)
      }
    }
    rafRef.current = requestAnimationFrame(animate)
    return () => cancelAnimationFrame(rafRef.current)
  }, [dream, prevDream, duration])

  return { displaySequence, displayAscii }
}

export function useInstrumentData(dream) {
  const [dreamMap, setDreamMap] = useState(null)
  const [generators, setGenerators] = useState(null)
  const [evolution, setEvolution] = useState(null)
  const [evoPopulation, setEvoPopulation] = useState(null)

  const loadMap = useCallback(() => {
    fetch(`${getApiBase()}/api/memory/map`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (data) setDreamMap(data)
      })
      .catch(() => {})
  }, [])

  const loadGenerators = useCallback(() => {
    fetch(`${getApiBase()}/api/generators`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (data) setGenerators(data)
      })
      .catch(() => {})
  }, [])

  const loadEvolution = useCallback(() => {
    Promise.all([
      fetch(`${getApiBase()}/api/evolution`).then((r) => (r.ok ? r.json() : null)),
      fetch(`${getApiBase()}/api/evolution/population`).then((r) =>
        r.ok ? r.json() : null
      ),
    ])
      .then(([evo, pop]) => {
        if (evo) setEvolution(evo)
        if (pop) setEvoPopulation(pop)
      })
      .catch(() => {})
  }, [])

  useEffect(() => {
    loadMap()
    loadGenerators()
    loadEvolution()
    const mapId = setInterval(loadMap, 60000)
    const genId = setInterval(loadGenerators, 30000)
    const evoId = setInterval(loadEvolution, 30000)
    return () => {
      clearInterval(mapId)
      clearInterval(genId)
      clearInterval(evoId)
    }
  }, [loadMap, loadGenerators, loadEvolution])

  useEffect(() => {
    if (dream?.generation) {
      loadMap()
      loadGenerators()
      loadEvolution()
    }
  }, [dream?.generation, loadMap, loadGenerators, loadEvolution])

  return {
    dreamMap,
    generators,
    evolution,
    evoPopulation,
    refresh: { loadMap, loadGenerators, loadEvolution },
  }
}
